<script lang="ts">
  /**
   * The tip for every keyword and status effect named in game text.
   *
   * Mounted once, in App. A keyword is only a span marked with what it is
   * (see Text), and this watches the whole document for one being pointed
   * at, tapped or focused. The glossary's technique list used to build a
   * tooltip component for each of its 428 keywords before any was opened.
   */
  import { onMount, tick } from 'svelte';
  import Icon from '../icon/icon.svelte';
  import Text from './text.svelte';
  import { claim, release, isTouchDevice, measuringStyle, placeTip } from './tip';
  import { keywordText, statusEffectInfo } from '../utils/text-vocabulary';
  import { ModifierTags } from '../models/modifier.type';

  type Shown = { anchor: HTMLElement; kind: 'keyword' | 'effect'; key: string };

  let shown = $state.raw<Shown | null>(null);
  let fading = $state.raw<Shown | null>(null);
  let tipEl: HTMLElement | null = $state(null);
  let placement = $state(measuringStyle);
  let fadeTimer: ReturnType<typeof setTimeout> | undefined;

  let current = $derived(shown ?? fading);
  let effect = $derived(current?.kind === 'effect' ? statusEffectInfo()[current.key] : null);
  let eyebrow = $derived(
    current?.kind === 'keyword'
      ? 'Keyword'
      : effect?.polarity === ModifierTags.Boon
        ? 'Boon'
        : effect?.polarity === ModifierTags.Bane
          ? 'Bane'
          : 'Status effect',
  );

  function anchorOf(target: EventTarget | null): HTMLElement | null {
    return target instanceof Element
      ? (target.closest('[data-tip-key]') as HTMLElement | null)
      : null;
  }

  async function open(anchor: HTMLElement) {
    if (shown?.anchor === anchor) return;
    const kind = anchor.dataset.tipKind === 'effect' ? 'effect' : 'keyword';
    const key = anchor.dataset.tipKey ?? '';

    clearTimeout(fadeTimer);
    fading = null;
    shown = { anchor, kind, key };
    claim({ anchor, close });

    placement = measuringStyle;
    await tick();
    if (shown?.anchor === anchor && tipEl) placement = placeTip(anchor, tipEl);
  }

  function close() {
    if (!shown) return;
    release(close);
    fading = shown;
    shown = null;
    fadeTimer = setTimeout(() => (fading = null), 300);
  }

  onMount(() => {
    const listeners: Array<[string, EventListener]> = [];
    const on = (type: string, handler: EventListener) => {
      document.addEventListener(type, handler);
      listeners.push([type, handler]);
    };

    if (isTouchDevice) {
      // Tap to open, tap again to close. A touch elsewhere closes it too,
      // through the one listener in tip.ts.
      on('click', (event) => {
        const anchor = anchorOf(event.target);
        if (!anchor) return;
        shown?.anchor === anchor ? close() : open(anchor);
      });
    } else {
      on('mouseover', (event) => {
        const anchor = anchorOf(event.target);
        if (anchor) open(anchor);
        else if (shown) close();
      });
    }

    // From the keyboard: a keyword can be tabbed to, and says what it is.
    // Focus that a finger or pointer caused is left to the click or hover
    // above; a tap focuses the keyword too, and opening on that focus let the
    // tap's click toggle the tip straight back shut.
    let pointerAt = 0;
    on('pointerdown', () => (pointerAt = performance.now()));
    on('focusin', (event) => {
      if (performance.now() - pointerAt < 500) return;
      const anchor = anchorOf(event.target);
      if (anchor) open(anchor);
    });
    on('focusout', (event) => {
      const next = (event as FocusEvent).relatedTarget;
      if (shown && !(next instanceof Node && shown.anchor.contains(next))) close();
    });
    on('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Escape') close();
    });

    return () =>
      listeners.forEach(([type, handler]) => document.removeEventListener(type, handler));
  });
</script>

{#if current}
  <div class="shadow" class:fading={!shown}></div>

  <div
    class="tip-container"
    class:fading={!shown}
    style={placement}
    bind:this={tipEl}
    id="game-tip"
    role="tooltip"
  >
    <div class="tip">
      <p class="eyebrow {effect?.polarity ?? ''}">{eyebrow}</p>
      <div class="heading">
        {#if effect?.icon}
          <Icon name={effect.icon} width="28px" height="28px" class="icon" />
        {/if}
        <h2>{effect?.title ?? current.key}</h2>
      </div>
      <Text data={(effect ? effect.text : keywordText(current.key)) ?? ''} interactive={false} />
    </div>
  </div>
{/if}

<style lang="less">
  @import '../../defines.less';

  // Drawn the same as Hover's tips, so the two cannot be told apart.
  .tip-container {
    position: fixed;
    z-index: 1000;
    max-width: min(420px, calc(100vw - 16px));
    box-sizing: border-box;
    pointer-events: none;

    border-image: url('../../assets/shadow.png') 10 / 10px;
    border-width: 7px;
    border-style: solid;

    animation: fadeIn 0.3s forwards;

    &.fading {
      animation: fadeOut 0.3s forwards;
    }
  }

  .tip {
    min-width: 200px;
    padding: 20px;
    text-align: left;
    line-height: 1.4;
    // Mounted outside <main>, which is where the game's light text colour is
    // set, so it has to bring its own.
    color: var(--light-font);
    background-color: var(--background-colour);
    outline: 1px solid black;
    border-radius: 4px;

    .eyebrow {
      margin: 0 0 4px;
      font-size: 0.75em;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      opacity: 0.75;

      &.boon {
        color: @green;
        opacity: 1;
      }
      &.bane {
        color: @red;
        opacity: 1;
      }
    }

    .heading {
      display: flex;
      align-items: center;
      grid-gap: 8px;
      margin-bottom: 6px;
    }

    h2 {
      margin: 0;
      line-height: 1.15;
      text-transform: capitalize;
    }
  }

  .shadow {
    position: fixed;
    inset: 0;
    z-index: 999;
    pointer-events: none;
    background: rgba(0, 0, 0, 0.3);
    animation: fadeIn 0.3s forwards;

    &.fading {
      animation: fadeOut 0.3s forwards;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
</style>
